import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { fetchBreedingPartners } from "../../../../api/partnership";
import { BreedingPartner } from "../../../../utils/breedingPartners";
import { sectionToIcon } from "../../../type/default";
import { DetailCard } from "./DetailCard";

export const BreedingPartnersCard = ({
  breedingId,
}: {
  breedingId: number;
}) => {
  const { t } = useTranslation();
  const [partners, setPartners] = useState<BreedingPartner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchBreedingPartners(breedingId).then((result) => {
      if (cancelled) return;
      setPartners(result.partners);
      setHasError(result.error !== null);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [breedingId]);

  return (
    <DetailCard
      title={t("section-partnerships")}
      icon={sectionToIcon.section_partnerships}
      action={
        <Link
          to="/breeder/partnerships"
          className="text-sm text-secondary hover:text-tertiary font-oxaniumMedium"
        >
          {t("breeding-detail-partners-manage")}
        </Link>
      }
    >
      {isLoading ? (
        <p className="text-sm text-grey-medium">{t("loading")}</p>
      ) : (
        <div className="space-y-3">
          {hasError && (
            <p role="alert" className="text-sm text-red-600">
              {t("breeding-detail-partners-error")}
            </p>
          )}
          {partners.length > 0 ? (
            <ul className="space-y-2">
              {partners.map((partner) => (
                <li
                  key={partner.partnershipId}
                  className="flex items-center gap-3 rounded-lg bg-grey-light/60 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="font-oxaniumMedium truncate">
                      {partner.companyName}
                    </p>
                    <p className="text-xs text-grey-medium">
                      {partner.partnershipType === "group"
                        ? t("group")
                        : t("installer")}
                    </p>
                  </div>
                  <span className="ml-auto shrink-0 rounded-full bg-white px-2 py-0.5 text-xs text-grey-readable">
                    {partner.scope === "full"
                      ? t("breeding-detail-partner-full")
                      : t("breeding-detail-partner-partial")}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            !hasError && (
              <p className="text-sm text-grey-medium">
                {t("breeding-detail-partners-empty")}
              </p>
            )
          )}
        </div>
      )}
    </DetailCard>
  );
};
